import { useParams, useNavigate } from "react-router-dom";
import './RegisteredUsersPage.css';

import { useEffect, useState } from "react";
import { getRegisteredUsers, markUserAsCheckedIn } from "../../services/UserInfo";
import { UserInfo } from "../../data/userInfo";
import {
    CircularProgress,
    Container,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Snackbar,
    Alert,
    Chip,
} from "@mui/material";

export default function RegisteredUsersPage() {
    const { eventId } = useParams<{ eventId: string }>();
    const navigate = useNavigate();
    const [users, setUsers] = useState<UserInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

    useEffect(() => {
        async function fetchUsers() {
            if (!eventId) {
                console.error("No eventId in URL");
                return;
            }

            try {
                const data = await getRegisteredUsers(eventId);
                console.log("Fetched users:", data);

                const sortedUsers = data.sort((a, b) => {
                    if (a.CheckedIn && !b.CheckedIn) return -1;
                    if (!a.CheckedIn && b.CheckedIn) return 1;
                    return 0;
                });

                setUsers(sortedUsers);
            } catch (error) {
                console.error("Error fetching users:", error);
            } finally {
                setLoading(false);
            }
        }

        fetchUsers();
    }, [eventId]);

    async function handleCheckIn(userId: string) {
        if (!eventId) return;

        try {
            await markUserAsCheckedIn(eventId, userId);

            setUsers((prevUsers) =>
                prevUsers.map((user) =>
                    user.Id === userId
                        ? { ...user, CheckedIn: true, CheckInTime: new Date().toISOString() }
                        : user
                ).sort((a, b) => {
                    if (a.CheckedIn && !b.CheckedIn) return -1;
                    if (!a.CheckedIn && b.CheckedIn) return 1;
                    return 0;
                })
            );

            setSnackbarMessage("User checked in successfully!");
        } catch (error) {
            console.error("Error checking in user:", error);
            setSnackbarMessage("Failed to check in user.");
        }
    }

    if (loading) {
        return (
            <Container sx={{ textAlign: "center", marginTop: 10 }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ marginTop: 2 }}>
                    Loading registered users...
                </Typography>
            </Container>
        );
    }

    if (!users.length) {
        return (
            <Container sx={{ textAlign: "center", marginTop: 10 }}>
                <Typography variant="h4" className="page-title">
                    No Users Registered Yet
                </Typography>
                <Typography variant="body1" sx={{ marginTop: 2 }}>
                    There are currently no participants registered for this event.
                </Typography>
            </Container>
        );
    }

    return (
        <Container sx={{ marginTop: 5 }}>
            <Typography variant="h4" className="page-title">
                Registered Users
            </Typography>

            <TableContainer component={Paper} className="users-table" sx={{ marginBottom: 3 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Full Name</strong></TableCell>
                            <TableCell><strong>Email</strong></TableCell>
                            <TableCell><strong>Check In Status</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user, idx) => (
                            <TableRow key={user.Email || idx}>
                                <TableCell>{user.FirstName} {user.LastName}</TableCell>
                                <TableCell>{user.Email}</TableCell>
                                <TableCell>
                                    {user.CheckedIn ? (
                                        <Chip
                                            label={`Checked In${user.CheckInTime ? ` at ${new Date(user.CheckInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}`}
                                            color="success"
                                            variant="outlined"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                    ) : (
                                        <>
                                            <Chip
                                                label="Not Checked In"
                                                color="default"
                                                variant="outlined"
                                                sx={{ fontWeight: 'bold', marginRight: 1 }}
                                            />
                                            <Button
                                                className="check-in-button"
                                                onClick={() => handleCheckIn(user.Id!)}
                                            >
                                                Check In
                                            </Button>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Snackbar
                open={!!snackbarMessage}
                autoHideDuration={3000}
                onClose={() => setSnackbarMessage(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="info" onClose={() => setSnackbarMessage(null)} className="snackbar-message">
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
}
